import { Download } from 'lucide-react';
import type { ReactElement } from 'react';

import { CompanyScopeNote } from '@/components/documents/CompanyScopeNote';
import { CardFrame, QueryBody } from '@/components/ui/card-frame';
import { useToast } from '@/components/ui/toast';
import { usePermission } from '@/hooks/usePermission';
import { useDocumentCompanyAccess } from '@/lib/document-company';
import { plural } from '@/lib/plural';

import {
  downloadErrorText,
  fileKindLabel,
  fileSizeLabel,
  versionStatusBadge,
} from '../presentation';
import type { ControlledFile, ControlledVersion } from '../types';
import { useControlledFileDownload } from '../use-controlled-files';
import { useControlledFiles } from '../use-controlled-files';

// Tarjeta "Documentos controlados" de la ficha (spec 2026-10-20 §6.9; 04-GC-P08 §6.C y
// §6.G): la copia con control de cambios, el editable y el PDF de cada versión aprobada,
// de acceso restringido a Calidad. Se consulta bajo la empresa activa: si no es la del
// documento, la tarjeta lo explica en vez de consultar.

const PERMISSION = 'quality:document:restricted-files';
const TITLE = 'Documentos controlados';

interface RowProps {
  file: ControlledFile;
  busy: boolean;
  onDownload: (file: ControlledFile) => void;
}

function FileRow({ file, busy, onDownload }: RowProps): ReactElement {
  return (
    <li className="cf-row">
      <div className="cf-file">
        <span className="cf-name">{file.fileName}</span>
        <span className="cf-meta">
          {fileKindLabel(file.kind)} · {fileSizeLabel(file.sizeBytes)}
        </span>
      </div>
      <button
        type="button"
        className="btn mut sm"
        aria-label={`Descargar ${file.fileName}`}
        disabled={busy}
        onClick={() => onDownload(file)}
      >
        <Download size={14} aria-hidden="true" />
        {busy ? 'Descargando…' : 'Descargar'}
      </button>
    </li>
  );
}

function VersionBlock({
  version,
  busyId,
  onDownload,
}: {
  version: ControlledVersion;
  busyId: string | null;
  onDownload: (file: ControlledFile) => void;
}): ReactElement {
  const badge = versionStatusBadge(version.status);
  const label = `Versión ${version.version}`;
  return (
    <div className="cf-version" role="group" aria-label={label}>
      <div className="cf-head">
        <strong>{label}</strong>
        <span className={`badge ${badge.className}`}>{badge.text}</span>
      </div>
      <ul className="cf-files">
        {version.files.map((f) => (
          <FileRow key={f.id} file={f} busy={busyId === f.id} onDownload={onDownload} />
        ))}
      </ul>
    </div>
  );
}

export function ControlledFilesCard({
  entryId,
  companyId,
}: {
  entryId: string;
  /** Empresa del documento. */
  companyId: string;
}): ReactElement | null {
  const canSee = usePermission(PERMISSION);
  const access = useDocumentCompanyAccess(companyId);
  const query = useControlledFiles(entryId, {
    companyId: access.kind === 'active' ? companyId : null,
    enabled: canSee && access.kind === 'active',
  });
  const download = useControlledFileDownload();
  const { showToast } = useToast();
  if (!canSee) {
    return null;
  }
  if (access.kind !== 'active') {
    return (
      <CardFrame title={TITLE} className="d3">
        <CompanyScopeNote access={access} subject="sus documentos controlados" />
      </CardFrame>
    );
  }
  const busyId = download.isPending ? (download.variables?.fileId ?? null) : null;
  const onDownload = (file: ControlledFile): void => {
    download.mutate(
      { entryId, fileId: file.id },
      {
        onSuccess: (data) => {
          // La URL firmada responde como adjunto: el navegador descarga sin dejar la ficha.
          window.location.assign(data.url);
          showToast(`Descarga de ${data.fileName} iniciada`);
        },
        onError: (err) => showToast(downloadErrorText(err, file.fileName), 'error'),
      },
    );
  };
  const count = query.data ? plural(query.data.versions.length, 'versión', 'versiones') : undefined;
  return (
    <CardFrame title={TITLE} count={count} className="d3">
      <QueryBody
        query={query}
        isEmpty={(d) => d.versions.length === 0}
        emptyText="Aparecen al aprobar una versión editada en línea: la copia con control de cambios, el editable y el PDF vigente."
      >
        {(d) => (
          <div className="cb cf-list">
            {d.versions.map((v) => (
              <VersionBlock key={v.version} version={v} busyId={busyId} onDownload={onDownload} />
            ))}
          </div>
        )}
      </QueryBody>
    </CardFrame>
  );
}
